// 인앱 안내 띠 자리 정하기(c015, 순수 로직 — DOM은 inAppBanner.ts). 게임 화면 좌표(px)의 HUD 사각형만 받는다.
// 띠: 위쪽 왼편(영웅 체력 묶음)과 오른편(지역 이름·일시정지·미니맵 묶음) 사이 빈 띠 가운데. 버튼까지 들어갈 폭이 없으면
// 예전처럼 화면 전체 폭 가운데로 넓히지 않고(좁은 폰 568×320에서 일시정지·지역 HUD를 덮었다) 아이콘만 있는 작은 칩을 빈 모퉁이에 둔다.

export interface Box { readonly left: number; readonly top: number; readonly right: number; readonly bottom: number }

/** 칩 크기(px): 아이콘 버튼 36 + 간격 6 + 닫기 34 + 안쪽 여백 4·4 + 테두리(여유 포함 88), 높이 42 */
export const CHIP = { w: 88, h: 42 } as const;
/** 띠·칩과 HUD 사이 여백(px) */
export const BANNER_GAP = 8;
/** 띠가 설명 글자까지 보이는 폭(px). 이보다 좁으면 버튼만 */
export const BANNER_TEXT_MIN = 250;
/** 띠의 버튼(주 버튼 + 닫기 + 안쪽 여백)이 들어가는 최소 폭(px, 안내 종류별). 이보다 좁으면 칩 */
export const BANNER_BUTTONS_MIN: Readonly<Record<'chrome' | 'kakao' | 'ios-menu', number>> = { chrome: 180, kakao: 200, 'ios-menu': 140 };
/** 띠 최대 폭(px) */
export const BANNER_MAX = 360;

export type BannerPlace =
  | { readonly mode: 'band'; readonly centerX: number; readonly maxWidth: number; readonly text: boolean }
  | { readonly mode: 'chip'; readonly left: number; readonly top: number };

function hits(a: Box, b: Box): boolean { return a.left < b.right && b.left < a.right && a.top < b.bottom && b.top < a.bottom; }
function free(c: Box, W: number, H: number, obstacles: readonly Box[]): boolean {
  if (c.left < 0 || c.top < 0 || c.right > W || c.bottom > H) return false;
  for (const o of obstacles) if (hits(c, o)) return false;
  return true;
}

/**
 * W·H: 게임 화면 크기, tl·tr: 위쪽 왼편·오른편 HUD 묶음(없으면 null — 타이틀), obstacles: 칩이 피할 HUD·입력 영역(일시정지·카메라·마을 패널·조이스틱 등).
 * kind: 안내 종류(버튼 폭). 띠가 들어가면 band, 아니면 빈 모퉁이의 chip(위쪽 빈 띠 → 오른쪽 위 묶음 아래 → 오른쪽 아래 → 왼쪽 아래 순).
 */
export function inAppBannerPlace(W: number, H: number, tl: Box | null, tr: Box | null, obstacles: readonly Box[], kind: keyof typeof BANNER_BUTTONS_MIN): BannerPlace {
  const lo = tl ? tl.right : 0, hi = tr ? tr.left : W;
  const avail = Math.max(0, hi - lo - BANNER_GAP * 2);
  if (avail >= BANNER_BUTTONS_MIN[kind]) {
    return { mode: 'band', centerX: Math.round((lo + hi) / 2), maxWidth: Math.min(BANNER_MAX, avail), text: avail >= BANNER_TEXT_MIN };
  }
  const g = BANNER_GAP, top = 4;
  const all = tl || tr ? [...(tl ? [tl] : []), ...(tr ? [tr] : []), ...obstacles] : obstacles;
  const cands: Box[] = [];
  // 1) 위쪽 빈 띠 가운데(칩은 들어간다)
  if (avail >= CHIP.w) { const cx = Math.round((lo + hi) / 2); cands.push({ left: cx - CHIP.w / 2, top, right: cx + CHIP.w / 2, bottom: top + CHIP.h }); }
  // 2) 오른쪽 위 묶음(미니맵) 바로 아래, 오른쪽 맞춤
  const right = tr ? tr.right : W - g;
  const below = tr ? tr.bottom + 6 : top;
  cands.push({ left: right - CHIP.w, top: below, right, bottom: below + CHIP.h });
  // 3) 오른쪽 아래 모퉁이, 4) 왼쪽 아래 모퉁이
  cands.push({ left: W - g - CHIP.w, top: H - g - CHIP.h, right: W - g, bottom: H - g });
  cands.push({ left: g, top: H - g - CHIP.h, right: g + CHIP.w, bottom: H - g });
  // 빈 모퉁이가 없으면(아주 작은 화면) 화면 안 빈자리를 훑고, 그래도 없으면 HUD와 가장 적게 겹치는 자리(PR #157 리뷰 P2)
  const c = findFreeBox(W, H, CHIP.w, CHIP.h, cands, all, [...(tl ? [tl] : []), ...(tr ? [tr] : [])]);
  return { mode: 'chip', left: c.left, top: c.top };
}

function overlapArea(a: Box, b: Box): number {
  const w = Math.min(a.right, b.right) - Math.max(a.left, b.left), h = Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top);
  return w > 0 && h > 0 ? w * h : 0;
}
/**
 * w×h 상자 자리: 후보를 순서대로 → 화면 안 8px 격자(위→아래, 오른쪽→왼쪽) → 그래도 없으면 장애물과 겹치는 넓이가 가장 작은 자리
 * (hard — 위쪽 HUD 묶음·일시정지 — 와 겹치는 넓이는 1000배로 쳐서 거의 피한다). obstacles에는 hard도 들어 있어야 한다.
 * 늘 화면 안(0..W−w, 0..H−h)으로 자른 자리를 돌려준다
 */
export function findFreeBox(W: number, H: number, w: number, h: number, cands: readonly Box[], obstacles: readonly Box[], hard: readonly Box[] = []): { left: number; top: number } {
  const maxL = Math.max(0, W - w), maxT = Math.max(0, H - h);
  const at = (l: number, t: number): Box => ({ left: l, top: t, right: l + w, bottom: t + h });
  for (const c of cands) if (free(c, W, H, obstacles)) return { left: Math.round(c.left), top: Math.round(c.top) };
  let best = { left: Math.round(maxL), top: 0 }, bestArea = Infinity;
  for (let t = 0; t <= maxT; t += 8) {
    for (let l = maxL; l >= 0; l -= 8) {
      const b = at(l, t);
      let area = 0;
      for (const o of obstacles) area += overlapArea(b, o);
      for (const o of hard) area += 999 * overlapArea(b, o);
      if (area === 0) return { left: Math.round(l), top: Math.round(t) };
      if (area < bestArea) { bestArea = area; best = { left: Math.round(l), top: Math.round(t) }; }
    }
  }
  return best;
}

/** 링크 복사 대체 주소 칸 크기(px, 칩 모드에서 칩 옆에 따로 띄울 때) */
export const COPY_FIELD = { w: 220, h: 32 } as const;
/**
 * 칩 모드 주소 칸 자리(PR #157 리뷰 P2): 칩 아래 → 위 → 왼쪽 순으로, 화면 안·HUD와 겹치지 않는 자리. 칩 안에 넣으면 칩이 늘어나 화면 밖·HUD 위로 나갔다
 */
export function copyFieldPlace(W: number, H: number, chip: Box, obstacles: readonly Box[]): Box {
  const w = Math.min(COPY_FIELD.w, Math.max(80, W - BANNER_GAP * 2)), h = COPY_FIELD.h, g = 4;
  const cands: Box[] = [
    { left: chip.right - w, top: chip.bottom + g, right: chip.right, bottom: chip.bottom + g + h },
    { left: chip.left, top: chip.bottom + g, right: chip.left + w, bottom: chip.bottom + g + h },
    { left: chip.right - w, top: chip.top - g - h, right: chip.right, bottom: chip.top - g },
    { left: chip.left - g - w, top: chip.top, right: chip.left - g, bottom: chip.top + h },
  ];
  const p = findFreeBox(W, H, w, h, cands, [chip, ...obstacles], [chip]);
  return { left: p.left, top: p.top, right: p.left + w, bottom: p.top + h };
}

/**
 * 링크 복사 대체 주소 칸 스타일(c015, c014 리뷰 13번): iOS Safari·WKWebView는 글자 16px 미만 입력 칸에 포커스하면 화면을 확대·이동한다.
 * 글자를 16px로 두고 칸 높이(32px)는 그대로다(작게 보일 필요가 없어 축소 변환은 쓰지 않는다)
 */
export const COPY_FIELD_FONT_PX = 16;
export function copyFieldCss(font: string, border: string): string {
  return 'pointer-events:auto;flex:1 1 160px;min-width:120px;height:32px;box-sizing:border-box;padding:0 8px;border-radius:8px;' +
    'border:1px solid ' + border + ';background:#0b1020;color:#fff;font:600 ' + COPY_FIELD_FONT_PX + 'px ' + font + ';user-select:text;-webkit-user-select:text;';
}
